import { ref } from 'vue';

export interface SocketOptions {
    /** WebSocket 连接地址 */
    url: string;
    /** 子协议 */
    protocols?: string | string[];
    /** 是否自动重连，默认 true */
    autoReconnect?: boolean;
    /** 初始重连间隔（ms），默认 3000 */
    reconnectInterval?: number;
    /** 最大重连间隔（ms），默认 60000 */
    maxReconnectInterval?: number;
    /** 最大重连次数，默认 10；0 表示无限 */
    maxReconnectAttempts?: number;
    /** 重连间隔递增系数，默认 2 */
    reconnectDecay?: number;
    /** 连接超时时间（ms），默认 10000 */
    timeout?: number;
    /** 心跳间隔（ms），默认 30000；0 表示关闭心跳 */
    heartbeatInterval?: number;
    /** 心跳消息内容，默认 'ping' */
    heartbeatMsg?: string | ArrayBufferLike | Blob;
    /** 连接成功回调 */
    onOpen?: (event: Event) => void;
    /** 收到消息回调 */
    onMessage?: (data: string) => void;
    /** 连接错误回调 */
    onError?: (event: Event) => void;
    /** 连接关闭回调 */
    onClose?: (event: CloseEvent) => void;
    /** 重连回调 */
    onReconnect?: (attempt: number) => void;
}

interface SocketMessage {
    role: 'user' | 'assistant';
    content: string;
}

export class SocketClient {
    private ws: WebSocket | null = null;
    private opts: Required<SocketOptions>;
    private reconnectCount = 0;
    private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
    private timeoutTimer: ReturnType<typeof setTimeout> | null = null;
    private manuallyClosed = false;
    private reconnecting = false;

    public ready = ref(false);
    public connecting = ref(false);

    constructor(options: SocketOptions) {
        this.opts = {
            protocols: '',
            autoReconnect: true,
            reconnectInterval: 3000,
            maxReconnectInterval: 60000,
            maxReconnectAttempts: 10,
            reconnectDecay: 2,
            timeout: 10000,
            heartbeatInterval: 30000,
            heartbeatMsg: 'ping',
            onOpen: () => {},
            onMessage: () => {},
            onError: () => {},
            onClose: () => {},
            onReconnect: () => {},
            ...options,
        };
    }

    connect(): void {
        if (this.ws?.readyState === WebSocket.CONNECTING || this.ws?.readyState === WebSocket.OPEN) return;

        this.manuallyClosed = false;
        this.connecting.value = true;
        this.clearTimeoutTimer();

        try {
            this.ws = new WebSocket(this.opts.url, this.opts.protocols || undefined);
        } catch (err) {
            this.connecting.value = false;
            this.scheduleReconnect();
            return;
        }

        this.timeoutTimer = setTimeout(() => {
            if (this.connecting.value && this.ws?.readyState !== WebSocket.OPEN) {
                this.ws?.close();
                this.connecting.value = false;
                this.scheduleReconnect();
            }
        }, this.opts.timeout);

        this.ws.onopen = (event: Event) => {
            this.clearTimeoutTimer();
            this.connecting.value = false;
            this.ready.value = true;
            this.reconnectCount = 0;
            this.startHeartbeat();
            this.opts.onOpen(event);
        };

        this.ws.onmessage = (event: MessageEvent) => {
            this.startHeartbeat();
            const data = typeof event.data === 'string' ? event.data : '';
            this.opts.onMessage(data);
        };

        this.ws.onerror = (event: Event) => {
            this.connecting.value = false;
            this.opts.onError(event);
        };

        this.ws.onclose = (event: CloseEvent) => {
            this.clearTimeoutTimer();
            this.ready.value = false;
            this.connecting.value = false;
            this.stopHeartbeat();
            this.opts.onClose(event);
            if (!this.manuallyClosed && this.opts.autoReconnect) {
                this.scheduleReconnect();
            }
        };
    }

    send(data: string | ArrayBufferLike | Blob | ArrayBufferView): boolean {
        if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(data as any);
            this.startHeartbeat();
            return true;
        }
        return false;
    }

    /** 主动关闭连接，不再自动重连 */
    close(code = 1000, reason = 'manual close'): void {
        this.manuallyClosed = true;
        this.clearReconnectTimer();
        this.stopHeartbeat();
        this.ws?.close(code, reason);
        this.ws = null;
        this.ready.value = false;
        this.connecting.value = false;
    }

    /** 中断当前连接并立即触发重连（用于停止当前流式输出后重新连接） */
    abort(): void {
        this.manuallyClosed = false;
        this.ws?.close(1000, 'abort');
        this.ws = null;
        this.ready.value = false;
        this.connecting.value = false;
        this.scheduleReconnect();
    }

    private scheduleReconnect(): void {
        if (this.reconnectTimer || this.manuallyClosed) return;
        if (this.opts.maxReconnectAttempts > 0 && this.reconnectCount >= this.opts.maxReconnectAttempts) return;

        this.reconnectCount += 1;
        const delay = Math.min(
            this.opts.reconnectInterval * Math.pow(this.opts.reconnectDecay, this.reconnectCount - 1),
            this.opts.maxReconnectInterval
        );

        this.opts.onReconnect(this.reconnectCount);
        this.reconnectTimer = setTimeout(() => {
            this.reconnectTimer = null;
            this.connect();
        }, delay);
    }

    private clearReconnectTimer(): void {
        if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
        }
    }

    private clearTimeoutTimer(): void {
        if (this.timeoutTimer) {
            clearTimeout(this.timeoutTimer);
            this.timeoutTimer = null;
        }
    }

    private startHeartbeat(): void {
        if (this.opts.heartbeatInterval <= 0) return;
        this.stopHeartbeat();
        this.heartbeatTimer = setInterval(() => {
            if (this.ws?.readyState === WebSocket.OPEN) {
                this.ws.send(this.opts.heartbeatMsg as any);
            }
        }, this.opts.heartbeatInterval);
    }

    private stopHeartbeat(): void {
        if (this.heartbeatTimer) {
            clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        }
    }
}

export type { SocketMessage };
